/**
 * makeColumns — 동적 N컬럼 빌더 (i18n 자동 적용) 유틸.
 *
 * 【역할】
 * - ProTable/antd Table 의 columns 배열을 `ColumnSpec[]` + `t()` 조합으로 선언적으로 생성.
 * - 페이지 코드 1 컬럼 당 1~3 줄로 경량화하고, 다국어/권한 숨김/정렬을 일관된 shape 로 처리.
 *
 * 【주요 기능】
 * - makeColumns(specs, t): ColumnSpec 을 antd ColumnType 으로 변환
 * - hidden 플래그로 권한별 컬럼 숨김 선언적 처리
 * - title 은 항상 t() 기반 → 언어 전환 즉시 반영
 * - sort/align/render 등 Ant Design ColumnType 속성 모두 pass-through
 *
 * 【ColumnSpec 필드 요약】
 * - key: i18n 라벨 키 (예: COL_CUSTOMER_ID)
 * - dataIndex: 문자열 또는 ['data','xxx'] 중첩 경로
 * - fallback: 번역 미존재 시 제목 대체
 * - width/minWidth/align/fixed/ellipsis/sorter/filters: antd 속성 직결
 * - render: 일반 함수 — t 캡처 필요 시 클로저로 래핑
 * - extra: 위에 없는 antd ColumnType 옵션 passthrough
 *
 * 【사용 예시】
 * ```tsx
 * import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
 * import { useLabel } from '@/framework/useLabel'
 *
 * const { t } = useLabel()
 * const specs: ColumnSpec<UserRow>[] = [
 *   { key: 'COL_LOGIN_ID',   dataIndex: ['data', 'loginId'],  width: 140 },
 *   { key: 'COL_USER_NAME',  dataIndex: ['data', 'userName'], width: 160 },
 *   { key: 'COL_USE_YN',     dataIndex: ['data', 'isActive'], width: 80, align: 'center',
 *     render: (v) => <Tag>{v === 1 ? t('LBL_YES','Y') : t('LBL_NO','N')}</Tag> },
 * ]
 * const columns = useMemo(() => makeColumns(specs, t), [t])
 * ```
 *
 * 【왜 이 패턴?】
 * - antd columns 를 매 페이지에서 수동 작성 시 i18n 누락/타입 불일치 빈번 발생.
 * - 공통 빌더로 "컬럼 정의 = 데이터 매핑" 을 한 곳에 몰아 유지보수성 확보.
 */
import type { ReactNode } from 'react'
import type { ColumnType, ColumnsType } from 'antd/es/table'

/** useLabel().t 의 시그니처 호환 타입 — fallback 2 번째 인자 허용. */
export type TranslateFn = (key: string, fallback?: string) => string

/** ColumnSpec — antd Table columns 를 선언하기 위한 경량 DTO. */
export interface ColumnSpec<T = unknown> {
  /** i18n label key. 예: 'COL_CUSTOMER_ID' */
  key: string
  /** antd dataIndex. 문자열 또는 중첩 경로 */
  dataIndex: string | readonly string[]
  /** 제목 번역 실패 시 fallback. 미지정 시 key 자체 표시 */
  fallback?: string
  /** 컬럼 너비 — antd 는 px(number)와 백분율/단위 문자열('40%')을 모두 받는다. */
  width?: number | string
  minWidth?: number
  align?: 'left' | 'center' | 'right'
  fixed?: 'left' | 'right'
  ellipsis?: boolean
  sorter?: boolean | ColumnType<T>['sorter']
  filters?: ColumnType<T>['filters']
  hidden?: boolean
  /** antd render 와 동일 시그니처 */
  render?: (value: unknown, row: T, index: number) => ReactNode
  /** Ant Design 추가 옵션 전달 (fixed width 외 기타) */
  extra?: Partial<ColumnType<T>>
}

/**
 * ColumnSpec 배열을 antd Table 의 ColumnsType 으로 변환.
 * @param specs - 선언적 컬럼 정의 배열.
 * @param t - useLabel().t 함수 — 컬럼 제목 번역용.
 * @returns antd Table 이 소비 가능한 columns 배열 (hidden=true 항목 제외).
 */
export function makeColumns<T = unknown>(
  specs: ReadonlyArray<ColumnSpec<T>>,
  t: TranslateFn
): ColumnsType<T> {
  const columns: ColumnsType<T> = []
  for (const s of specs) {
    if (s.hidden) continue
    const col: ColumnType<T> = {
      key: typeof s.key === 'string' ? s.key : undefined,
      title: t(s.key, s.fallback ?? s.key),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- antd dataIndex 는 string | string[] 수용 — readonly string[] 호환을 위해 any cast
      dataIndex: s.dataIndex as any,
      ...(s.width !== undefined && { width: s.width }),
      ...(s.minWidth !== undefined && ({ minWidth: s.minWidth } as Partial<ColumnType<T>>)),
      ...(s.align && { align: s.align }),
      ...(s.fixed && { fixed: s.fixed }),
      ...(s.ellipsis !== undefined && { ellipsis: s.ellipsis }),
      ...(s.sorter !== undefined && { sorter: s.sorter as ColumnType<T>['sorter'] }),
      ...(s.filters && { filters: s.filters }),
      ...(s.render && { render: s.render as ColumnType<T>['render'] }),
      ...s.extra
    }
    columns.push(col)
  }
  return columns
}
